import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, AlertTriangle, Zap } from 'lucide-react';
import { ScanForm } from '../components/scanner';
import { useScan, useFileScan } from '../hooks/useScanner';
import { Layout } from '../components/layout';

interface FeatureCardProps {
  icon: typeof CheckCircle;
  title: string;
  description: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon: Icon, title, description }) => (
  <div className="card">
    <div className="card-body">
      <Icon size={32} style={{ color: 'var(--color-accent)', marginBottom: 'var(--space-4)' }} />
      <h3 style={{
        fontFamily: 'var(--font-display)',
        fontWeight: 700,
        marginBottom: 'var(--space-2)',
        textTransform: 'uppercase',
        letterSpacing: '0.025em',
      }}>
        {title}
      </h3>
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-muted)' }}>
        {description}
      </p>
    </div>
  </div>
);

export const ScannerPage: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const scanMutation = useScan();
  const fileScanMutation = useFileScan();

  const handleScan = async (data: { url: string; region?: string; industry?: string }) => {
    setError(null);
    try {
      const result = await scanMutation.mutateAsync({
        url: data.url,
        options: {
          regions: data.region ? [data.region] : undefined,
          industry: data.industry,
        },
      });
      navigate(`/scanner/${result.scanId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start scan');
    }
  };

  const handleFileScan = async (file: File, region: string, industry: string) => {
    setError(null);
    try {
      const result = await fileScanMutation.mutateAsync({ file, region, industry });
      navigate(`/scanner/${result.scanId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to scan files');
    }
  };

  return (
    <Layout>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: 'var(--space-8) var(--space-4)' }}>
        {/* Hero Section */}
        <div style={{ textAlign: 'center', maxWidth: '768px', margin: '0 auto var(--space-12)' }}>
          <div style={{
            display: 'inline-flex',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--color-accent)',
            marginBottom: 'var(--space-4)',
          }}>
            <Shield size={48} style={{ color: 'var(--color-bg)' }} />
          </div>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-4xl)',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.025em',
            marginBottom: 'var(--space-4)',
          }}>
            Compliance Scanner
          </h1>
          <p style={{
            fontSize: 'var(--text-lg)',
            color: 'var(--color-muted)',
            marginBottom: 'var(--space-6)',
          }}>
            Check your website for Google Ads policy compliance before running ads.
            Identify issues that could get your ads disapproved.
          </p>
        </div>

        {/* Scan Form */}
        <div style={{ maxWidth: '640px', margin: '0 auto var(--space-12)' }}>
          <ScanForm
            onSubmit={handleScan}
            onFileSubmit={handleFileScan}
            isLoading={scanMutation.isPending || fileScanMutation.isPending}
          />
          {error && (
            <div style={{
              marginTop: 'var(--space-4)',
              padding: 'var(--space-4)',
              backgroundColor: '#fee2e2',
              border: '2px solid var(--color-error)',
              color: '#991b1b',
              fontSize: 'var(--text-sm)',
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Features */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 'var(--space-6)',
          maxWidth: '960px',
          margin: '0 auto',
        }}>
          <FeatureCard
            icon={CheckCircle}
            title="30+ Compliance Checks"
            description="Comprehensive scanning for placeholder content, fake testimonials, missing legal pages, and more."
          />
          <FeatureCard
            icon={AlertTriangle}
            title="Clear Remediation"
            description="Get specific guidance on how to fix each issue with links to relevant Google policies."
          />
          <FeatureCard
            icon={Zap}
            title="Instant Reports"
            description="Download detailed PDF, JSON, or Markdown reports to share with your team."
          />
        </div>
      </div>
    </Layout>
  );
};

export default ScannerPage;
